<script setup lang="ts">
import type { TPilotingPlanningScaling } from "@/components/piloting/planning/piloting-planning.types";
import type { ProgrammingRangeDeletionTarget } from "@/components/piloting/planning/section-programming-ranges-cell.types";
import type { ISiteSectionsVirtualizedTableNode } from "@/components/site/site-sections-virtualized-table/site-sections-virtualized-table.types";
import type { TSite } from "@/data/api/kili/sites/types/sites.type";
import type { SiteSectionCategoryId, SiteSectionId, V4HSiteId } from "@/data/common/ids";
import type {
  CreateProgrammingRangeBody,
  UpdateSiteSectionProgrammingRangeRequest,
} from "@/domains/v4b/site-programming-range/site-programming-range.types";
import type {
  ProgrammingRange as PlanningProgrammingRange,
  SectionNodePlanning,
} from "@/domains/v4b/site-programming-range/site-programming-range.types";

import type { TCustomBreadcrumbRouteFormatter } from "@/components/layout/base-layout-breadcrumb.vue";

import { cn, walkTreeByLevel } from "@voltalis/frontend-utils";
import { SectionAppliancesCountByProgrammingCategory } from "@voltalis/ui-kit/components";
import { useElementBounding } from "@vueuse/core";
import { remove } from "es-toolkit";
import { isEmptyObject } from "es-toolkit";
import { storeToRefs } from "pinia";
import { computed, ref, useTemplateRef } from "vue";
import { watch } from "vue";

import { useProgrammingRangeDeletion } from "@/app/control/program-planning/use-programming-range-deletion";
import { useSiteSectionsPlanningSelection } from "@/app/control/program-planning/use-site-sections-planning-selection";
import { ActiveFilter } from "@/components/section/site-section-active-filters.types";
import { useManageSiteSectionCategories } from "@/composables/kili/sections/use-manage-site-section-categories";
import { useCreateSiteProgrammingRangeMutation } from "@/composables/kili/site-programming-range/mutations/use-create-site-programming-range-mutation";
import { useUpdateSiteProgrammingRangeMutation } from "@/composables/kili/site-programming-range/mutations/use-update-site-programming-range-mutation";
import { useGetSiteSectionsPlanningQuery } from "@/composables/kili/site-programming-range/queries/use-get-site-sections-planning-query";
import { useGetSiteDetailsQuery } from "@/composables/kili/sites/queries/use-get-site-details-query";
import { useGetSitesQuery } from "@/composables/kili/sites/queries/use-get-sites-query";
import { useToaster } from "@/composables/layout/use-toaster";
import { useBrandedNumberRouteParams } from "@/composables/use-route-params/use-hospitality-number-route-param";
import { useUserPermission } from "@/composables/user-permission/use-user-permission";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { ERouteName } from "@/router/router.type";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import { useProgrammingPlanningStore } from "@/stores/piloting/programming-planning-store";
import { EDateScaling } from "@/utils/date";
import { countAllLeafDescendants } from "@/utils/site-section/site-section-utils";

import Skeleton from "primevue/skeleton";

import BaseLayout from "@/components/layout/base-layout.vue";
import PlanningDateRangeHeaderCell from "@/components/piloting/planning/planning-date-range-header-cell.vue";
import PlanningDateRangeNavigator from "@/components/piloting/planning/planning-date-range-navigator.vue";
import PlanningFilters from "@/components/piloting/planning/planning-filters.vue";
import PlanningLegend from "@/components/piloting/planning/planning-legend.vue";
import PlanningTodayHeaderCell from "@/components/piloting/planning/planning-today-header-cell.vue";
import PlanningToolbar from "@/components/piloting/planning/planning-toolbar.vue";
import ProgrammingRangeFormDialog from "@/components/piloting/planning/programming-range/programming-range-form-dialog.vue";
import SectionProgrammingRangesCell from "@/components/piloting/planning/section-programming-ranges-cell.vue";
import PilotingWizard from "@/components/piloting/wizard/piloting-wizard.vue";
import SiteSectionCategoryActiveFilters from "@/components/section/site-section-active-filters.vue";
import SiteSectionsVirtualizedTable from "@/components/site/site-sections-virtualized-table/site-sections-virtualized-table.vue";
import ButtonGroup, { type IButtonGroupOption } from "@/components/ui/buttons/button-group.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import IndeterminateCheckbox from "@/components/ui/checkbox/indeterminated-checkbox.vue";
import ConfirmationDialog from "@/components/ui/dialog/confirmation-dialog.vue";

const ITEM_HEIGHT = 70; // in pixels
const currentRouteName = computed(() => router.currentRoute.value.name);

const { brandedNumberRouteParam: siteId } = useBrandedNumberRouteParams<V4HSiteId>("siteId");
const tableContainer = useTemplateRef("tableContainer");
const { height: tableContainerHeight } = useElementBounding(tableContainer);
const siteSectionsVirtualizedTable = useTemplateRef<HTMLElement>("siteSectionsVirtualizedTable");
const { height: tableHeight } = useElementBounding(siteSectionsVirtualizedTable);
const tableHeaderFirstRow = useTemplateRef("tableHeaderFirstRow");
const { height: tableHeaderFirstRowHeight } = useElementBounding(tableHeaderFirstRow);
const { t, k } = useTypedIntl();
const toast = useToaster();

const programmingPlanningStore = useProgrammingPlanningStore();
const { planningRangeScale } = storeToRefs(programmingPlanningStore);

const { isCurrentUserOperator } = useUserPermission();

const { data: sites } = useGetSitesQuery();
const { data: siteDetails, isPending: isSiteDetailsPending } = useGetSiteDetailsQuery(siteId);
const { data: sectionsPlanning, isPending: isSectionsPlanningPending } = useGetSiteSectionsPlanningQuery(siteId);
const { mutateAsync: createSiteProgrammingRange, isPending: isCreateProgrammingRangePending } =
  useCreateSiteProgrammingRangeMutation(siteId);
const { mutateAsync: updateSiteProgrammingRange, isPending: isUpdateProgrammingRangePending } =
  useUpdateSiteProgrammingRangeMutation();

const forceUpdateKey = ref<number>(0);
const filteredSiteSectionTree = ref<ISiteSectionsVirtualizedTableNode<SectionNodePlanning>[]>([]);
const isPreviewingAllSectionsSelect = ref<boolean>(false);
const previewedSectionsSelectIds = ref<Set<SiteSectionId>>(new Set());

const isToolbarVisible = ref<boolean>(false);

const isProgrammingRangeFormDialogVisible = ref<boolean>(false);
const sectionForEdition = ref<SiteSectionId | undefined>();
const programmingRangeForEdition = ref<PlanningProgrammingRange | undefined>();

const isPilotingWizardVisible = ref<boolean>(false);

const isProgrammingRangeMutationPending = computed<boolean>(() => {
  return isCreateProgrammingRangePending.value || isUpdateProgrammingRangePending.value;
});

const {
  isConfirmationDialogVisible: isDeleteProgrammingRangeConfirmationDialogVisible,
  isDeletionPending,
  requestDeletion: requestProgrammingRangeDeletion,
  confirmDeletion: confirmProgrammingRangeDeletion,
} = useProgrammingRangeDeletion();

const maybeSite = computed<TSite | undefined>(() => {
  return sites.value?.find((site) => site.siteId === siteId.value);
});

const breadcrumbFormatter = computed<TCustomBreadcrumbRouteFormatter>(() => {
  return {
    [ERouteName.PLANNING]: () => {
      return maybeSite.value?.name ?? t(k.control.piloting.planning.title);
    },
  };
});

const selectedSectionCategoryIds = ref<SiteSectionCategoryId[]>([]);

const { sectionCategoryFilterOptions } = useManageSiteSectionCategories(siteId, selectedSectionCategoryIds);

const activeCategoryFilters = computed<ActiveFilter<"section", SiteSectionCategoryId>[]>(() =>
  selectedSectionCategoryIds.value.flatMap((categoryId) => {
    const option = sectionCategoryFilterOptions.value.find((categoryOption) => categoryOption.id === categoryId);

    return option
      ? [
          {
            type: "section" as const,
            id: categoryId,
            label: option.label,
          },
        ]
      : [];
  }),
);

const rangeScaleOptions: IButtonGroupOption<TPilotingPlanningScaling>[] = [
  {
    label: k.global.units.temporal.DAY({ count: 0 }),
    value: EDateScaling.DAY,
  },
  {
    label: k.global.units.temporal.WEEK({ count: 0 }),
    value: EDateScaling.WEEK,
  },
  {
    label: k.global.units.temporal.MONTH({ count: 0 }),
    value: EDateScaling.MONTH,
  },
  {
    label: k.global.units.temporal.QUARTER({ count: 0 }),
    value: EDateScaling.QUARTER,
  },
];

function mapSectionToVirtualizedTreeNode(
  sectionNode: SectionNodePlanning,
): ISiteSectionsVirtualizedTableNode<SectionNodePlanning> {
  return {
    id: sectionNode.sectionId,
    key: sectionNode.sectionId,
    name: sectionNode.name,
    state: {
      isExpanded: true,
    },
    children: sectionNode.children.map(mapSectionToVirtualizedTreeNode),
    data: sectionNode,
  };
}

const mappedSiteSectionTree = computed<ISiteSectionsVirtualizedTableNode<SectionNodePlanning>[]>(() => {
  return sectionsPlanning.value?.map(mapSectionToVirtualizedTreeNode) ?? [];
});

async function handleFilterResult(result: ISiteSectionsVirtualizedTableNode<SectionNodePlanning>[]) {
  filteredSiteSectionTree.value = result;
  forceUpdateKey.value++;
  unselectAllSections();
}

const {
  selectedSectionsIds,
  deepToggleLeafSectionSelection,
  isSectionSelected,
  selectAllState,
  toggleSelectAllSections,
  unselectAllSections,
} = useSiteSectionsPlanningSelection(filteredSiteSectionTree);

function handleSelectSection(section: ISiteSectionsVirtualizedTableNode<SectionNodePlanning>) {
  deepToggleLeafSectionSelection(section);
}

function handlePreviewSectionSelect(section: ISiteSectionsVirtualizedTableNode<SectionNodePlanning>) {
  previewedSectionsSelectIds.value.add(section.data.sectionId);

  for (const child of walkTreeByLevel(section.children)) {
    previewedSectionsSelectIds.value.add(child.data.sectionId);
  }
}

function resetPreviewSectionSelect() {
  previewedSectionsSelectIds.value.clear();
}

function isSectionPreviewedForSelection(section: ISiteSectionsVirtualizedTableNode<SectionNodePlanning>): boolean {
  if (previewedSectionsSelectIds.value.has(section.data.sectionId)) return true;
  return false;
}

function removeSectionCategoryFromFilters(categoryId: SiteSectionCategoryId) {
  remove(selectedSectionCategoryIds.value, (c) => c === categoryId);

  MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_FILTER_CLEARED, {
    page_context: currentRouteName.value?.toString(),
    method: "single",
  });
}

function handleCloseToolbar() {
  isToolbarVisible.value = false;
  unselectAllSections();
}

function handleEditSectionProgrammingRange(section: SectionNodePlanning, range: PlanningProgrammingRange) {
  sectionForEdition.value = section.sectionId;
  programmingRangeForEdition.value = range;

  isProgrammingRangeFormDialogVisible.value = true;
}

function resetEditSelection() {
  sectionForEdition.value = undefined;
  programmingRangeForEdition.value = undefined;
}

async function handleCreateProgrammingRange(range: CreateProgrammingRangeBody) {
  try {
    const sectionIds: SiteSectionId[] = Array.from(selectedSectionsIds.value);

    await createSiteProgrammingRange({
      sectionIds,
      programmingRange: range,
    });

    toast.add({
      severity: "success",
      detail: t(k.control.piloting.planning.createProgrammingRange.success),
    });

    isProgrammingRangeFormDialogVisible.value = false;

    MixpanelService.trackEvent(EMixpanelEvent.PROGRAMMING_RANGE_CREATED, {
      space_id: sectionIds,
      program_id: range.programId,
      start_date: range.start,
      end_date: range.end ?? "indeterminate",
      filter_space_category_id: selectedSectionCategoryIds.value,
    });
  } catch {
    toast.add({
      severity: "error",
      detail: t(k.control.piloting.planning.createProgrammingRange.error),
    });
  }
}

async function handleUpdateProgrammingRange(request: UpdateSiteSectionProgrammingRangeRequest) {
  if (sectionForEdition.value === undefined) return;

  try {
    await updateSiteProgrammingRange({
      siteId: siteId.value,
      sectionId: sectionForEdition.value,
      ...request,
    });

    toast.add({
      severity: "success",
      detail: t(k.control.piloting.planning.updateProgrammingRange.success),
    });

    isProgrammingRangeFormDialogVisible.value = false;

    MixpanelService.trackEvent(EMixpanelEvent.PROGRAMMING_RANGE_UPDATED);
  } catch {
    toast.add({
      severity: "error",
      detail: t(k.control.piloting.planning.updateProgrammingRange.error),
    });
  }
}

function handleDeleteSectionProgrammingRange(sectionId: SiteSectionId, target: ProgrammingRangeDeletionTarget) {
  requestProgrammingRangeDeletion({
    command: {
      siteId: siteId.value,
      sectionId,
      programmingRangeUuid: target.uuid,
    },
    programmingRangeEnd: target.end,
    programId: target.programId,
  });
}

watch(
  selectedSectionsIds,
  (newSelection) => {
    if (newSelection.size > 0) {
      isToolbarVisible.value = true;
      return;
    }

    isToolbarVisible.value = false;
  },
  { deep: true },
);
</script>

<template>
  <BaseLayout
    :breadcrumb-formatter="breadcrumbFormatter"
    :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.view"
    :full-bleed="isToolbarVisible"
    :hide-breadcrumb="isToolbarVisible"
  >
    <PlanningToolbar
      v-model:visible="isToolbarVisible"
      :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.toolbar"
      :selected-sections-nb="selectedSectionsIds.size"
      @close="handleCloseToolbar"
      @apply-program="isProgrammingRangeFormDialogVisible = true"
    />

    <div :class="cn('flex flex-col h-full w-full', { 'px-8': isToolbarVisible })">
      <div class="bg-[#FDFDFD] rounded-xl w-full h-full my-6 p-6 site-sections-planning flex flex-col gap-4">
        <div class="flex justify-between items-center">
          <div class="flex flex-col gap-1">
            <h5>{{ t(k.control.piloting.planning.title) }}</h5>
            <small>{{ t(k.control.piloting.planning.subtitle) }}</small>
          </div>

          <div class="flex items-center gap-3">
            <HospitalityButton
              v-if="isCurrentUserOperator"
              :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.legacyPlanningOverrideButton"
              severity="tertiary"
              size="sm"
              @click="isPilotingWizardVisible = true"
            >
              <span class="font-semibold text-text-brand">
                {{ t(k.control.piloting.planning.accessLegacyPiloting) }}
              </span>
            </HospitalityButton>

            <PlanningFilters
              v-model:selectedSectionCategoryIds="selectedSectionCategoryIds"
              :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.filters.component"
              :mapped-site-section-tree="mappedSiteSectionTree"
              :section-category-filter-options="sectionCategoryFilterOptions"
              @filter-section-tree="handleFilterResult"
            />

            <ButtonGroup
              v-model="planningRangeScale"
              :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.planningRangeScaleButtonGroup"
              :options="rangeScaleOptions"
            />
          </div>
        </div>

        <SiteSectionCategoryActiveFilters
          v-if="activeCategoryFilters.length"
          :active-category-filters="activeCategoryFilters"
          class="border-b pb-4"
          show-active-filters-text
          @remove-section-category="(filter) => removeSectionCategoryFromFilters(filter.id)"
        />

        <div class="h-full w-full relative">
          <div
            class="absolute inset-0"
            ref="tableContainer"
          >
            <Suspense>
              <template #fallback>
                <Skeleton
                  class="!w-full !h-full"
                  :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.skeleton"
                />
              </template>

              <SiteSectionsVirtualizedTable
                ref="siteSectionsVirtualizedTable"
                :class="{
                  'preview-all-sections-select': isPreviewingAllSectionsSelect,
                }"
                :key="forceUpdateKey"
                :item-height="ITEM_HEIGHT"
                :is-loading="isSectionsPlanningPending"
                :viewport-height="tableContainerHeight"
                :site-sections-forest="filteredSiteSectionTree"
              >
                <template #header>
                  <tr ref="tableHeaderFirstRow">
                    <th>
                      <div class="flex justify-between px-3 items-center">
                        <HospitalityButton
                          class="!px-2 !py-1 !h-fit"
                          :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.todayButton"
                          severity="secondary"
                          @click="programmingPlanningStore.resetDate"
                        >
                          <i class="pi pi-calendar !text-xs mr-1" />
                          <small class="!text-[12px] font-bold">{{ t(k.global.misc.today) }}</small>
                        </HospitalityButton>

                        <PlanningDateRangeNavigator />
                      </div>
                    </th>

                    <th class="p-0">
                      <PlanningDateRangeHeaderCell />
                    </th>
                  </tr>

                  <tr>
                    <th>
                      <div class="flex items-center px-3 py-2 gap-2">
                        <IndeterminateCheckbox
                          class="cursor-pointer"
                          :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.selectAllCheckbox"
                          input-id="select-all-checkbox"
                          aria-labelledby="site-name-label"
                          :model-value="selectAllState"
                          @click="toggleSelectAllSections"
                          @mouseenter="isPreviewingAllSectionsSelect = true"
                          @mouseleave="isPreviewingAllSectionsSelect = false"
                        />
                        <i class="pi pi-home !text-xs text-brand font-medium" />
                        <i class="pi pi-angle-right !text-xs text-text-secondary" />
                        <Skeleton
                          v-if="isSiteDetailsPending"
                          :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.siteNameLoader"
                        />
                        <label
                          v-else
                          class="font-semibold cursor-pointer"
                          :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.siteName"
                          for="select-all-checkbox"
                          id="site-name-label"
                          @mouseenter="isPreviewingAllSectionsSelect = true"
                          @mouseleave="isPreviewingAllSectionsSelect = false"
                        >
                          {{ siteDetails?.name ?? "-" }}
                        </label>
                      </div>
                    </th>
                    <th class="p-0">
                      <PlanningTodayHeaderCell :x-plotline-height="tableHeight - tableHeaderFirstRowHeight - 2" />
                    </th>
                  </tr>
                </template>

                <template #bodyAdditionalTd="{ item: section }">
                  <td class="p-0">
                    <SectionProgrammingRangesCell
                      :section-with-programming-ranges="section.data"
                      @click:delete="handleDeleteSectionProgrammingRange(section.data.sectionId, $event)"
                      @click:edit="handleEditSectionProgrammingRange(section.data, $event)"
                    />
                  </td>
                </template>

                <template #beforeNodeCellToggleIcon="{ item: section }">
                  <IndeterminateCheckbox
                    :class="
                      cn('cursor-pointer', {
                        'is-preview-select': isSectionPreviewedForSelection(section),
                      })
                    "
                    :data-section-id="section.id"
                    :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.sectionNode.selectSectionCheckbox"
                    :input-id="`section-checkbox-${section.id}`"
                    :aria-labelledby="`section-label-${section.id}`"
                    :model-value="isSectionSelected(section)"
                    @click="handleSelectSection(section)"
                    @mouseenter="handlePreviewSectionSelect(section)"
                    @mouseleave="resetPreviewSectionSelect()"
                  />
                </template>

                <template #nodeCellContent="{ item: section }">
                  <div class="tree-node-item gap-1 flex flex-col">
                    <div class="flex gap-1">
                      <label
                        class="font-semibold cursor-pointer"
                        :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.sectionNode.sectionName"
                        :for="`section-checkbox-${section.id}`"
                        :id="`section-label-${section.id}`"
                        @mouseenter="handlePreviewSectionSelect(section)"
                        @mouseleave="resetPreviewSectionSelect()"
                      >
                        {{ section.name }}
                      </label>
                      <span
                        v-if="section.children?.length"
                        :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.sectionNode.sectionChildrenCount"
                      >
                        ({{ countAllLeafDescendants(section) }})
                      </span>
                    </div>

                    <div
                      v-if="section.data.category || !isEmptyObject(section.data.applianceCountByProgrammingCategory)"
                      class="flex gap-2"
                    >
                      <small
                        v-if="section.data.category"
                        class="bg-gray-100 rounded-xl px-2 h-[23px] flex items-center font-semibold"
                        :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.sectionNode.sectionCategory"
                        >{{ section.data.category?.name }}</small
                      >
                      <SectionAppliancesCountByProgrammingCategory
                        :appliance-count-by-programming-category="section.data.applianceCountByProgrammingCategory"
                        :show-count="false"
                      />
                    </div>
                  </div>
                </template>
              </SiteSectionsVirtualizedTable>
            </Suspense>
          </div>
        </div>

        <PlanningLegend :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.planningLegend" />
      </div>
    </div>
  </BaseLayout>

  <ProgrammingRangeFormDialog
    v-model:visible="isProgrammingRangeFormDialogVisible"
    :is-loading="isProgrammingRangeMutationPending"
    :programming-range="programmingRangeForEdition"
    :selected-program-count="selectedSectionsIds.size"
    :site-id="siteId"
    @hide="resetEditSelection"
    @create-programming-range="handleCreateProgrammingRange($event)"
    @update-programming-range="handleUpdateProgrammingRange($event)"
  />

  <ConfirmationDialog
    v-model="isDeleteProgrammingRangeConfirmationDialogVisible"
    :callback="confirmProgrammingRangeDeletion"
    :is-loading="isDeletionPending"
    delete-mode
    :subtitle="t(k.control.piloting.planning.deleteProgrammingRange.subtitle)"
    :title="t(k.control.piloting.planning.deleteProgrammingRange.title)"
  />

  <PilotingWizard
    v-model:visible="isPilotingWizardVisible"
    :site="maybeSite"
  />
</template>

<style class="scss">
.site-sections-planning {
  .site-sections-virtualized-table {
    &.preview-all-sections-select {
      .indeterminated-checkbox {
        &:not(.checked) {
          .indeterminated-checkbox-box {
            @apply border-brand;
          }
        }
      }
    }

    table {
      @apply border-collapse;

      thead {
        tr {
          box-shadow: inset 0 -1px var(--color-neutral-light); /* border bottom */

          th:not(:last-child) {
            box-shadow: inset -1px 0 var(--color-neutral-light); /* border right */
          }
        }
      }

      tbody {
        tr > td:not(:last-child) {
          box-shadow: inset -1px 0 var(--color-neutral-light);
        }

        .virtualized-table-node-cell {
          @apply px-3 w-[335px] !important;
        }
      }

      .indeterminated-checkbox {
        &:not(.checked) {
          .indeterminated-checkbox-box {
            &:hover {
              @apply border-brand;
            }
          }
        }

        &.is-preview-select {
          .indeterminated-checkbox-box {
            @apply border-brand;
          }
        }
      }
    }
  }
}
</style>
